import {
  Button,
  Collapse,
  Group,
  NumberInput,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import { Product } from "../../../../gql/__generated__/graphql";
import BudgetCalculator from "../../../components/BudgetCalculator/BudgetCalculator";
import ProductSelector from "../../../components/ProductSelector/ProductSelector";
import { CampaignFormValues } from "./Campaign.types";

interface CampaignFormProps {
  products: Product[];
  onSubmit: (values: CampaignFormValues) => void;
  onCancel: () => void;
}

function CampaignForm({ products, onSubmit, onCancel }: CampaignFormProps) {
  const minMediaCount = 5;

  const [opened, { toggle }] = useDisclosure(false);
  const [suggestedBudget, setSuggestedBudget] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<CampaignFormValues>({
    initialValues: {
      name: "",
      products: [],
      videosDesired: 5,
      // streamsDesired: 5,
      affiliateGMVMinimum: undefined,
      suggestedBudget: undefined,
      actualBudget: undefined,
    },
    validate: {
      name: (value) =>
        value.trim().length < 3
          ? "Campaign name must be at least 3 characters"
          : null,
      products: (value) =>
        value.length === 0
          ? "Select at least one product"
          : value.length > 5
          ? "Maximum 5 products allowed"
          : null,
      videosDesired: (value, values) => {
        if (value < 0) return "Cannot be negative";
        if (value > 1000) return "Maximum 1000 videos";
        if (value < minMediaCount)
          return `Total videos must be at least ${minMediaCount}`;
        return null;
      },
      // streamsDesired: (value, values) => {
      //   if (value < 0) return "Cannot be negative";
      //   if (value > 1000) return "Maximum 1000 streams";
      //   if ((values.videosDesired || 0) + value < minMediaCount)
      //     return `Total videos and streams must be at least ${minMediaCount}`;
      //   return null;
      // },
      affiliateGMVMinimum: (value) => {
        if (value !== undefined && value < 0) return "Cannot be negative";
        return null;
      },
      actualBudget: (value, values) => {
        if (value === undefined || value === null) return "Budget is required";
        if (value <= 0) return "Budget must be greater than 0";
        if (values.suggestedBudget && value < values.suggestedBudget * 0.8)
          return "Budget is too low compared to suggestion";
        return null;
      },
    },
  });

  const handleBudgetCalculated = (calculatedBudget: number) => {
    setSuggestedBudget(calculatedBudget);
    form.setFieldValue("suggestedBudget", calculatedBudget);
    form.setFieldValue("actualBudget", calculatedBudget);
  };

  // Custom budget calculation for campaigns that includes streams
  const calculateCampaignBudget = () => {
    // Simple budget calculation logic:
    // Base: $20 per video, $30 per stream
    // Add 20% if GMV minimum is set
    // Add $10 per product beyond the first one
    const videoCost = form.values.videosDesired * 20;
    // const streamCost = form.values.streamsDesired * 30;
    const streamCost = 0;
    const gmvFactor = form.values.affiliateGMVMinimum ? 1.2 : 1;
    const productFactor = (form.values.products.length - 1) * 10;

    const calculatedBudget = Math.round(
      (videoCost + streamCost) * gmvFactor + productFactor
    );

    return calculatedBudget;
  };

  const handleSubmit = (values: CampaignFormValues) => {
    setIsSubmitting(true);
    onSubmit(values);
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md">
        <TextInput
          label="Campaign Name"
          placeholder="Enter campaign name"
          required
          {...form.getInputProps("name")}
        />

        <ProductSelector
          products={products}
          selectedProductIds={form.values.products}
          onChange={(selectedIds) =>
            form.setFieldValue("products", selectedIds)
          }
        />

        <Group grow>
          <NumberInput
            label="Videos Desired"
            description="Number of videos you want creators to make"
            placeholder="Enter number of videos"
            min={0}
            max={1000}
            required
            {...form.getInputProps("videosDesired")}
          />

          {/* <NumberInput
            label="Streams Desired"
            description="Number of livestreams you want creators to do"
            placeholder="Enter number of streams"
            min={0}
            max={1000}
            required
            {...form.getInputProps("streamsDesired")}
          /> */}
        </Group>

        <Group>
          <Button variant="subtle" onClick={toggle} size="compact-md">
            {opened ? "Hide Advanced Options" : "Show Advanced Options"}
          </Button>
        </Group>

        <Collapse in={opened}>
          <Paper p="md" withBorder>
            <Stack>
              <Title order={5}>Advanced Options</Title>
              <NumberInput
                label="Affiliate GMV Minimum ($)"
                description="Minimum gross merchandise value each creator must generate"
                placeholder="Enter minimum GMV"
                min={0}
                {...form.getInputProps("affiliateGMVMinimum")}
              />
              <Text size="xs" c="dimmed">
                Note: Setting GMV minimum will make your campaign more expensive
              </Text>
            </Stack>
          </Paper>
        </Collapse>

        <BudgetCalculator
          mode="campaign"
          videosDesired={form.values.videosDesired}
          productsCount={form.values.products.length}
          affiliateGMVMinimum={!!form.values.affiliateGMVMinimum}
          isValid={form.isValid("products") && form.isValid("videosDesired")}
          onBudgetCalculated={handleBudgetCalculated}
        />

        {suggestedBudget !== null && (
          <NumberInput
            label="Your Campaign Budget ($)"
            description="You can adjust this, but we recommend staying close to the suggestion"
            placeholder="Enter your budget"
            min={1}
            required
            {...form.getInputProps("actualBudget")}
          />
        )}

        <Group mt="xl">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              !form.isValid() || suggestedBudget === null || isSubmitting
            }
            loading={isSubmitting}
          >
            Launch Campaign
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

export default CampaignForm;
